/**
 * productCache.js
 * Reusable localStorage helpers for product analytics data.
 * TTL: 30 minutes (1_800_000 ms)
 */

const CACHE_TTL_MS = 30 * 60 * 1000; // 30 minutes

/**
 * Build a deterministic cache key from user ID and date range.
 * Including userId ensures different accounts on the same browser
 * never share or overwrite each other's cached data.
 *
 * @param {string|number} userId
 * @param {string}        startDate
 * @param {string}        endDate
 * @returns {string}
 */
export const buildCacheKey = (userId, startDate, endDate) =>
    `product_metadata-${userId}-${startDate}-${endDate}`;

/**
 * Build a deterministic cache key for grouped (group_by) product analytics data.
 * Keyed separately from flat data because the API response shape differs.
 *
 * @param {string|number} userId
 * @param {string}        startDate
 * @param {string}        endDate
 * @param {string}        groupBy   – e.g. "parent" | "tag"
 * @returns {string}
 */
export const buildGroupedCacheKey = (userId, startDate, endDate, groupBy) =>
    `product_grouped-${userId}-${startDate}-${endDate}-${groupBy}`;

/**
 * Build a deterministic cache key for product analytics totals.
 * Keyed separately from the product list because the data shape differs.
 *
 * @param {string|number} userId
 * @param {string}        startDate
 * @param {string}        endDate
 * @param {string}        [skuParam]  – comma-separated SKU filter (if any)
 * @returns {string}
 */
export const buildTotalsCacheKey = (userId, startDate, endDate, skuParam) =>
    `product_totals-${userId}-${startDate}-${endDate}${skuParam ? `-${skuParam}` : ''}`;

/**
 * Check if a stored timestamp is older than TTL.
 * @param {number} timestamp  – value stored alongside the data
 * @returns {boolean}
 */
export const isCacheExpired = (timestamp) => {
    if (!timestamp || typeof timestamp !== 'number') return true;
    return Date.now() - timestamp > CACHE_TTL_MS;
};

/**
 * Read a cache entry. Returns null when the key is missing or the TTL has
 * been exceeded (stale entries are removed automatically).
 * Works for both array data (product lists) and object data (totals).
 * @param {string} key
 * @returns {Array|Object|null}  – the cached data, or null
 */
export const getProductCache = (key) => {
    try {
        const raw = localStorage.getItem(key);
        if (!raw) return null;

        const parsed = JSON.parse(raw);

        if (!parsed || typeof parsed !== 'object') {
            localStorage.removeItem(key);
            return null;
        }

        if (isCacheExpired(parsed.timestamp)) {
            localStorage.removeItem(key);
            return null;
        }

        return parsed.data ?? null;
    } catch {
        // Corrupted entry – clean up and signal a miss.
        try { localStorage.removeItem(key); } catch { /* ignore */ }
        return null;
    }
};

/**
 * Write data to localStorage under the given key with the current
 * timestamp so TTL can be checked on the next read.
 * @param {string}       key
 * @param {Array|Object} data
 * @returns {boolean}  – true on success, false if storage is unavailable/full
 */
export const setProductCache = (key, data) => {
    try {
        const entry = { data, timestamp: Date.now() };
        localStorage.setItem(key, JSON.stringify(entry));
        return true;
    } catch {
        // QuotaExceededError or localStorage unavailable.
        return false;
    }
};

/**
 * Explicitly evict a single cache entry.
 * @param {string} key
 */
export const clearProductCache = (key) => {
    try { localStorage.removeItem(key); } catch { /* ignore */ }
};

/**
 * Remove ALL product_metadata-* entries from localStorage.
 */
export const clearAllProductCaches = () => {
    try {
        Object.keys(localStorage)
            .filter(k => k.startsWith('product_metadata-') || k.startsWith('product_grouped-') || k.startsWith('product_totals-'))
            .forEach(k => localStorage.removeItem(k));
    } catch { /* ignore */ }
};